import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Newspaper } from "lucide-react";
import api from "../api";
import { useLanguage } from "../i18n";
import { Reveal } from "./Reveal";
import { isStandaloneApp } from "../utils/pwa";

const imgSrc = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export function HomeBlogGrid() {
  const { t, lang } = useLanguage();
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    let alive = true;
    setPosts(null);
    api.get(`/blog/posts?lang=${lang}&limit=8`)
      .then(({ data }) => { if (alive) setPosts(data.data || []); })
      .catch(() => { if (alive) setPosts([]); });
    return () => { alive = false; };
  }, [lang]);

  // Nel sito web il blog è in evidenza; nella PWA installata resta fuori dall'esperienza app.
  if (isStandaloneApp()) return null;
  if (posts && posts.length === 0) return null;

  return (
    <section data-testid="home-blog-section" className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto pb-16 sm:pb-24">
      <Reveal>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
          <div>
            <div className="font-mono-t text-[11px] font-bold uppercase tracking-[0.3em] text-[#FF6B35] mb-4 flex items-center gap-2">
              <Newspaper className="w-4 h-4" /> {t("home.blogEyebrow")}
            </div>
            <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900">{t("blog.title")}</h2>
            <p className="mt-4 max-w-xl text-slate-600">{t("blog.sub")}</p>
          </div>
          <Link to="/blog" data-testid="home-blog-view-all"
            className="inline-flex w-fit shrink-0 items-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-3 font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-700 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B35] focus-visible:ring-offset-2">
            {t("home.blogViewAll")} <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </Reveal>

      <div data-testid="home-blog-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {posts === null ? (
          Array.from({ length: 8 }).map((_, i) => (
            <div key={i} data-testid={`home-blog-skeleton-${i}`}
              className="aspect-square rounded-3xl bg-white border border-slate-200/70 shadow-sm animate-pulse" />
          ))
        ) : (
          posts.map((p, i) => (
            <Reveal key={p.post_id} delay={i * 0.05} className="h-full">
              <Link to={`/blog/${p.slug}`} aria-label={p.title} data-testid={`home-blog-card-${p.slug}`}
                className="group relative block aspect-square overflow-hidden rounded-3xl bg-[#1E293B] shadow-sm hover:-translate-y-1 hover:shadow-xl hover:shadow-orange-100 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B35] focus-visible:ring-offset-2">
                {p.image ? (
                  <img src={imgSrc(p.image)} alt={p.title} loading="lazy" data-testid={`home-blog-image-${p.slug}`}
                    className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" />
                ) : (
                  <div data-testid={`home-blog-image-${p.slug}`}
                    className="absolute inset-0 bg-gradient-to-br from-[#1E293B] via-[#334155] to-[#FF6B35]/70 flex items-center justify-center">
                    <Newspaper className="w-10 h-10 text-white/40" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/25 to-transparent" />
                <div className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/15 backdrop-blur-sm text-white flex items-center justify-center group-hover:bg-[#FF6B35] transition-colors">
                  <ArrowUpRight className="w-4 h-4" />
                </div>
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <h3 data-testid={`home-blog-title-${p.slug}`}
                    className="font-display font-bold text-lg leading-snug text-white line-clamp-3 group-hover:text-[#FFEDD5] transition-colors">
                    {p.title}
                  </h3>
                </div>
              </Link>
            </Reveal>
          ))
        )}
      </div>
    </section>
  );
}
